import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchOrders } from '../services/orders.service';
import { Order } from '../types';
import { isFiberPrepOrder, isFtthOrder } from '../utils';
import { convertDateFormat } from '../utils/helpers';

interface LoadOrdersProps {
  buildingId: string;
  building: string[];
  stair: string[];
  floor: string[];
  outletReference: string[];
  hexacleAddress: string;
}

interface OrderListState {
  lastApiError: string | undefined;
  isOrderLoading: boolean;
  orderList: Order[] | undefined;
}

const initialState: OrderListState = {
  isOrderLoading: false,
  orderList: undefined,
  lastApiError: undefined
};

export const loadOrders = createAsyncThunk<Order[], LoadOrdersProps, { rejectValue: Error }>(
  'orderList/loadOrders',
  async (params, { rejectWithValue }) => {
    const { buildingId, building, stair, floor, outletReference, hexacleAddress } = params;
    try {
      const response = await fetchOrders(
        buildingId,
        building,
        stair,
        floor,
        outletReference,
        hexacleAddress
      );
      return response.data.map((order) => {
        if (isFtthOrder(order)) {
          return {
            ...order,
            createdDate: convertDateFormat(order.createdDate),
            intervention: {
              ...order.intervention,
              interventionDate: convertDateFormat(order.intervention.interventionDate),
              reportDate: convertDateFormat(order.intervention.reportDate),
              commissioningReportDate: convertDateFormat(order.intervention.commissioningReportDate)
            },
            termination: {
              ...order.termination,
              date: convertDateFormat(order.termination.date)
            }
          };
        }
        if (isFiberPrepOrder(order)) {
          return {
            ...order,
            createdDate: convertDateFormat(order.createdDate),
            appointment: {
              ...order.appointment,
              date: convertDateFormat(order.appointment.date)
            }
          };
        }
        return order;
      });
    } catch (e) {
      const error = e as Error;
      if (!error.message) {
        throw error;
      }
      return rejectWithValue(error);
    }
  }
);

const orderListSlice = createSlice({
  name: 'orderList',
  initialState,
  reducers: {
    resetOrderList: () => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadOrders.pending, (state) => {
        state.isOrderLoading = true;
        state.orderList = undefined;
        state.lastApiError = undefined;
      })
      .addCase(loadOrders.fulfilled, (state, { payload }) => {
        state.isOrderLoading = false;
        state.orderList = payload || [];
        state.lastApiError = undefined;
      })
      .addCase(loadOrders.rejected, (state, { error }) => {
        state.isOrderLoading = false;
        state.lastApiError = error.message;
        state.orderList = undefined;
      });
  }
});

export const { resetOrderList } = orderListSlice.actions;

export default orderListSlice.reducer;
